<template>
  <div :class="_getClass">
    <VButton
      v-if="!customComponent"
      type="primary"
      size="regular"
      iconPosition="right"
      :label="processingRender ? 'Rendering preview...' : 'PDF Preview'"
      :inverted="true"
      :onClickHandler="_onClick"
      :disabled="processingRender || disabled"
    ></VButton>

    <component
      v-else
      :is="customComponent"
      v-bind="$props"
      :onClickHandler="_onClick"
      :disabled="processingRender || disabled"
    />

    <v-modal
      v-if="customDataRenderModalOpened"
      title="Enter Preview Info"
      :actions="_modalActions"
      classType="primary"
      version="new"
      extraClass="in-preview-renderer"
    >
      <div class="preview-template">
        <VRadioInputGroup
          :checked="currentMethod"
          name="previewSendingMethods"
          :items="previewMethods"
          extraClass="in-preview-renderer"
          @change="value => onPreviewMethodChange(value)"
        ></VRadioInputGroup>

        <div v-show="currentMethod === 'list'">
          <span class="title">Select contact</span>
          <VForm
            :name="contactsListFormName"
            :fields="fields.contactsList"
            @change="contact => this.onContactSelect(contact.value)"
          ></VForm>
        </div>

        <div v-show="currentMethod === 'custom'">
          <span class="title">Contact info</span>
          <VForm
            :name="cutomContactFormName"
            :fields="fields.customContact"
          ></VForm>
        </div>

        <div v-if="this.fields.mergeParams.length">
          <WithLoader :isLoading="isContactsLoading">
            <span class="title">Enter merge params</span>
            <VForm
              :fields="fields.mergeParams"
              :name="mergeParamsFormName"
              hideSubmit
            >
            </VForm>
          </WithLoader>
        </div>
      </div>
    </v-modal>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import { fetchContactsList } from "@/api-client/contacts";

// mixins
import withForm from "@/mixins/withForm";
import withToasted from "@/mixins/withToasted";

import { required } from "@/utils/validations";

// components
import VButton from "@/components/VButton";
import VModal from "@/components/VModal";
import VRadioInputGroup from "@/components/VRadioInputGroup";
import VVueSelect from "@/components/VVueSelect";
import WithLoader from "@/components/WithLoader";

import betaIconSrc from "@/assets/images/beta.svg";

//constants
import { testPreviewContactId } from "@/config";
import { addressFormFields } from "@/constants/forms";

export default Vue.component("VPreviewRenderedTemplate", {
  props: {
    contactId: VueTypes.string.required,
    templateId: VueTypes.string.required,
    templateType: VueTypes.oneOf(["postcard", "letter"]).required,
    template: VueTypes.object,
    onClickHandler: VueTypes.any.def(null),
    disabled: VueTypes.bool.def(false),
    label: VueTypes.string.def("Render Template"),
    imageSrc: VueTypes.string.def(betaIconSrc),
    extraClass: VueTypes.string.def(""),
    customComponent: VueTypes.oneOfType([VueTypes.func, VueTypes.any]).def(null)
  },

  components: {
    VButton,
    VModal,
    VRadioInputGroup,
    VVueSelect
  },

  mixins: [withForm, withToasted],

  data() {
    return {
      customDataRenderModalOpened: false,
      processingRender: false,
      contactsListFormName: "contactsList",
      mergeParamsFormName: "preview_template_merge_params",
      cutomContactFormName: "customContact",
      mergeParams: [],
      testPreviewContactId,
      contacts: [],
      contactFromList: "",
      currentMethod: "test",
      previewMethods: [
        { label: "Use test contact", value: "test" },
        {
          label: "Choose from contacts list",
          value: "list"
        },
        { label: "Enter contact manually", value: "custom" }
      ]
    };
  },

  computed: {
    _template() {
      const templateKey = Object.keys(this.template).length
        ? "template"
        : "_internalTemplate";
      return this[templateKey];
    },

    _getClass() {
      return `v-preview-rendered__wrapper ${this.extraClass}`;
    },

    _onClick() {
      return this.onClickHandler
        ? this.onClickHandler
        : this._processRenderPreview;
    },
    _modalActions() {
      return [
        {
          label: "Cancel",
          value: "cancel",
          type: "primary",
          size: "wide",
          noBorder: true,
          inverted: true,
          click: () => (this.customDataRenderModalOpened = false)
        },
        {
          label: this.processingRender ? "Rendering preview..." : "Continue",
          value: "add",
          type: "primary",
          size: "regular",
          click: this._renderPreviewTemplateFromModal,
          disabled: this.shouldRenderBtnDisable
        }
      ];
    },

    shouldRenderBtnDisable() {
      if (this.processingRender) {
        return true;
      }
      if (this.currentMethod === "list" && !this.contactFromList) {
        return true;
      }
      if (
        this.currentMethod === "custom" &&
        !this.isFormValid("customContact")
      ) {
        return true;
      }
    },

    fields() {
      return {
        contactsList: [
          {
            name: "contactsListField",
            elementType: "vue-select",
            selectItems: this.contactsToSelect,
            placeholder: "Select contact",
            onSearchAsyncHandler: this.onSearch
          }
        ],
        mergeParams: this.mergeParams.map(item => ({
          label: item.toUpperCase(),
          name: item,
          classType: "primary",
          placeholder: item
        })),

        customContact: [
          {
            name: "first_name",
            placeholder: "First Name",
            label: "First Name",
            validate: [required],
            isRequired: true
          },
          {
            name: "last_name",
            placeholder: "Last Name",
            label: "Last Name",
            validate: [required],
            isRequired: true
          },
          ...addressFormFields.map(item => {
            // if (item.name === "country") {
            //   item.extraClassRoot = "campaigns-create";
            // }

            item.layout = "full";
            item.classType = "primary";
            return item;
          })
        ]
      };
    },

    contactsToSelect() {
      return this.contacts.map(contact => {
        return {
          key: contact.id,
          value: contact.id,
          label: `${contact.first_name} ${contact.last_name}`
        };
      });
    },

    getContactId() {
      //if user already choose contact from list
      //and option radio button "list" is active
      if (this.contactFromList && this.currentMethod === "list") {
        return this.contactFromList;
      }

      //else looking for contact id in props
      else if (this.contactId) {
        return this.contactId;
      }

      //else return test id
      else {
        return this.testPreviewContactId;
      }
    },

    ...mapGetters("contacts", { isContactsLoading: "isLoading" })
  },

  methods: {
    /**
     * Look for a merge data within HTML string
     * all of the variables withn %% %% treats as
     * merge param
     * @param html
     * @param unique
     * @returns {Array}
     * @private
     */
    _findMergeParamsRawHtml(html, unique = true) {
      const pattern = /%%.*?%%/g;
      let merge_params = [];
      let current;

      while ((current = pattern.exec(html))) {
        merge_params.push(current[0].replace(/%%/g, "").trim());
      }

      if (unique) {
        merge_params = [...new Set(merge_params)];
      }

      return merge_params;
    },

    /**
     * Look for a merge data within Template object (Builder)
     * all of the merge (dynamic) variables
     * @param template
     * @param unique
     * @private
     */
    _findMergeParamTemplateObject(template, unique = true) {
      const {
        front: { children: frontChildren },
        back: { children: backChildren }
      } = template;

      // concatenate front and back sides children
      const childrens = [...frontChildren, ...backChildren];

      // perform getting all of the mergeParams within the childrens

      let mergeParams = childrens
        .filter(item => item.text)
        .map(item => item.text)
        .flat()
        .filter(item => item.attributes.mergeParameter)
        .map(item => item.attributes.mergeParameter);

      if (unique) {
        // removing duplicated keys
        mergeParams = [...new Set(mergeParams)];
      }

      return mergeParams;
    },

    /**
     * Method for triggering preview from merge params modal
     * @returns {Promise<void>}
     * @private
     */
    async _renderPreviewTemplateFromModal() {
      let values = this.getFormValues(this.mergeParamsFormName) || {};
      if (this.currentMethod === "custom") {
        this.validateForm("customContact");
        let customContact = this.getFormValues(this.cutomContactFormName);
        customContact.name = `${customContact.first_name} ${customContact.last_name}`;
        delete customContact.first_name;
        delete customContact.last_name;
        values = {
          ...values,
          $to: customContact
        };
      }

      //if current renderer method is "custom"
      //check form "customContact" valid
      if (
        this.currentMethod !== "custom" ||
        (this.currentMethod === "custom" && this.isFormValid("customContact"))
      ) {
        await this._triggerPreview(values, () => {
          this.customDataRenderModalOpened = false;
        });
      }
    },

    async _processRenderPreview() {
      let custom_fields = [];

      if (this.templateType === "postcard") {
        const { data_type } = this._template || {};

        if (!data_type) {
          console.warn("Cannot use preview. No template.");
          return;
        }
        if (data_type === "html") {
          // processing raw html
          const { front_html, back_html } = this._template;
          custom_fields = this._findMergeParamsRawHtml(front_html + back_html);
        } else {
          // processing builder template
          custom_fields = this._findMergeParamTemplateObject(this._template);
        }
      } else {
        const { type, html } = this._template || {};

        if (type === "html") {
          custom_fields = this._findMergeParamsRawHtml(html);
        }
      }

      //prevent recurring fields
      if (this.currentMethod !== "custom") {
        custom_fields = custom_fields.filter(
          field =>
            ![
              "first_name",
              "last_name",
              "company",
              "address_line_1",
              "address_line_2",
              "address_city",
              "address_country",
              "address_state",
              "address_zip"
            ].includes(field)
        );
      }

      this.mergeParams = custom_fields;

      this.customDataRenderModalOpened = true;
    },

    async _triggerPreview(merge_params = {}, cb) {
      this.processingRender = true;

      await this.triggerTestPreview({
        data: {
          merge_params,
          template_id: this._template.id,
          contact_id: this.getContactId,
          product_type:
            this.templateType === "letter" && this._template.product_code_id
              ? "custom"
              : this.templateType === "letter"
              ? "#10"
              : this.templateType
        },

        onSuccess: pdf_url => {
          this.processingRender = false;

          if (pdf_url) {
            window.open(pdf_url, "_blank");
          }

          cb && cb();
        },

        onError: () => {
          this.processingRender = false;

          this.toast(
            "Rendering preview failed. Please, try again later.",
            "error"
          );

          cb && cb();
        }
      });
    },
    onSearch(search, loading) {
      if (search) {
        loading(true);
        fetchContactsList({
          page: 1,
          search: search,
          page_size: 25
        }).then(resp => {
          this.contacts = resp.items;
          loading(false);
        });
      }
    },

    onPreviewMethodChange(method) {
      this.currentMethod = method;
      this.clearForm(this.mergeParamsFormName);
      this.clearForm(this.contactsListFormName);
    },

    onContactSelect(contactId) {
      this.clearForm(this.mergeParamsFormName);
      this.contactFromList = contactId;
      this.fetchContact({
        id: contactId,
        onSuccess: resp => {
          const customData = resp.custom_data;
          const arrOfCustomDataKeys = Object.keys(resp.custom_data);
          arrOfCustomDataKeys.forEach(key =>
            this.updateValue({
              formName: this.mergeParamsFormName,
              fieldName: key,
              value: customData[key]
            })
          );
        }
      });
    },

    ...mapActions("templates", ["triggerTestPreview", "fetchTemplateById"]),
    ...mapActions("contacts", ["fetchContact"]),
    ...mapActions("forms", ["updateValue"])
  },

  async created() {
    if (this.templateId && Object.keys(this.template).length === 0) {
      this._internalTemplate = await this.fetchTemplateById({
        id: this.templateId
      });
    }
    fetchContactsList({
      page: 1,
      // sort: this.sort,
      // search: this.search,
      page_size: 25
    }).then(resp => (this.contacts = resp.items));
  }
});
</script>

<style lang="scss" scoped>
.v-preview-rendered__wrapper {
  display: inline-block;
}

.custom-data__form-wrapper {
  overflow: auto;

  & /deep/ .v-input__label-wrapper {
    @include textCropEllipsis();

    label {
      flex-shrink: 1;
    }
  }
}
</style>

<style lang="scss" scoped>
.preview-template {
  @include customScrollbar();
  overflow: auto;
  height: 90%;
  padding-right: 15px;
  flex-grow: 10;
}
.title {
  display: block;
  padding-top: 20px;
  padding-bottom: 15px;
  font-family: Lato-Bold, sans-serif;
}
</style>

<style lang="scss">
.in-preview-renderer {
  .v-radio-input__wrapper {
    margin-bottom: 22px;
  }

  &.v-modal__container {
    min-height: 500px;
  }
}
</style>
